import React, { ReactNode } from "react";

import { Modal } from "@UI/Components/Modal/Modal";
import { VStack } from "@UI/Components/Stack/Stack";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Link } from "@UI/Components/Link/Link";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import { useRoutes } from "@Libs/Router/hooks/useRoutes";
import messages from "../messages";

export interface IEditServiceRequestModalProps {
  isOpen: boolean;
  /** "Return": dismiss and stay on the task detail page. */
  onClose: () => void;
  /** "Continue": let the edit navigation through. */
  onContinue: () => void;
}

/**
 * Warns before editing a new-flow service request that a provider has
 * already seen: category, subcategory, and audience can't change from here.
 */
export const EditServiceRequestModal = ({
  isOpen,
  onClose,
  onContinue,
}: IEditServiceRequestModalProps) => {
  const { generateRoute } = useRoutes();

  const linkFormatter = (...chunks: ReactNode[]) => (
    <Link
      href={generateRoute(["SERVICE_REQUEST_CREATION"])}
      color="pink.500"
      fontWeight="500"
    >
      {chunks}
    </Link>
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <VStack spacing={4} textAlign="center">
        <Heading size="md">
          <FormattedMessage {...messages["editServiceRequestModal.title"]} />
        </Heading>
        <Text color="gray.600" align="left">
          <FormattedMessage {...messages["editServiceRequestModal.body"]} />
        </Text>
        <Text color="gray.600" align="left">
          <FormattedMessage
            {...messages["editServiceRequestModal.bodySecondary"]}
            values={{ link: linkFormatter }}
          />
        </Text>
        <VStack spacing={3} width="100%" paddingTop="8px">
          <Button width="100%" colorScheme="seaGreen" onClick={onContinue}>
            <FormattedMessage
              {...messages["editServiceRequestModal.continueButton"]}
            />
          </Button>
          <Button
            width="100%"
            colorScheme="gray"
            variant="secondary"
            onClick={onClose}
          >
            <FormattedMessage
              {...messages["editServiceRequestModal.returnButton"]}
            />
          </Button>
        </VStack>
      </VStack>
    </Modal>
  );
};
